import React, { PureComponent } from 'react'
import { connect, ConnectedProps } from 'react-redux'
import { ThunkDispatch } from '@reduxjs/toolkit'
import {
  DsBox,
  DsPopup,
  DsRemixIcon,
  DsStack,
  DsTypography
} from '@am92/react-design-system'

import withPageConstants, {
  IWithPageConstantsProps
} from '~/src/Hocs/withPageConstants'
import withRouter, { IWithRouterProps } from '~/src/Hocs/withRouter'

import PageTitle from '~/src/Components/PageTitle'

import ADULT_WELCOME_POPUP_PAGE_CONSTANTS from './AdultNoChildrenWelcomePopup.PageConstants'
import { T_ADULT_WELCOME_POPUP_PAGE_CONSTANTS } from './AdultNoChildrenWelcomePopup.PageConstants/TYPES'

import { getPlatformUsersSelector } from '~/src/Redux/PlatformUsers/Selectors'
import { isServiceLoading } from '~/src/Redux/ServiceTracker/Selectors'
import { getUserReducer } from '~/src/Redux/User/Selectors'
import getMyChildrenService, {
  getMyChildrenServiceName
} from '~/src/Redux/User/Services/getMyChildren.Service'

import APP_ROUTES from '~/src/Constants/APP_ROUTES'

export interface IAdultNoChildrenWelcomePopupProps
  extends IWithPageConstantsProps<T_ADULT_WELCOME_POPUP_PAGE_CONSTANTS>,
    IWithRouterProps,
    PropsFromRedux {}

interface IAdultNoChildrenWelcomePopupState {
  localDismissed: boolean
  hasFetched: boolean
}

class AdultNoChildrenWelcomePopup extends PureComponent<
  IAdultNoChildrenWelcomePopupProps,
  IAdultNoChildrenWelcomePopupState
> {
  state: IAdultNoChildrenWelcomePopupState = {
    localDismissed: false,
    hasFetched: false
  }

  async componentDidMount() {
    const { actions } = this.props
    await actions.getMyChildrenService()
    this.setState({ hasFetched: true })
  }

  handleCreateChildClick = () => {
    this.setState({ localDismissed: true })
    this.props.navigateTo(APP_ROUTES.CHILDREN_NEW.pathname)
  }

  handleClose = () => {
    this.setState({ localDismissed: true })
  }

  renderStepCard = (iconClass: string, stepTitle: string, stepBody: string) => {
    return (
      <DsStack
        spacing='var(--ds-spacing-frostbite)'
        direction='column'
        alignItems='center'
        textAlign='center'
        sx={{
          flex: 1,
          p: 'var(--ds-spacing-bitterCold)',
          border: '1px solid var(--ds-colour-strokeDefault)',
          borderRadius: '16px',
          backgroundColor: 'var(--ds-colour-surfacePrimary)',
          minWidth: { xs: '100%', md: '180px' }
        }}
      >
        <DsBox
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '56px',
            height: '56px',
            borderRadius: '50%',
            border: '2px solid var(--ds-colour-strokeSelected)',
            color: 'var(--ds-colour-strokeSelected)',
            fontSize: '24px'
          }}
        >
          <DsRemixIcon className={iconClass} />
        </DsBox>
        <DsTypography variant='headingBoldExtraSmall'>{stepTitle}</DsTypography>
        <DsTypography variant='bodyRegularSmall' color='text.secondary'>
          {stepBody}
        </DsTypography>
      </DsStack>
    )
  }

  renderPopupContent = () => {
    const { PAGE_CONSTANTS } = this.props
    return (
      <DsStack
        spacing='var(--ds-spacing-pleasant)'
        direction='column'
        sx={{ width: '100%' }}
      >
        <DsTypography
          variant='bodyRegularLarge'
          sx={{ mb: 'var(--ds-spacing-frostbite)' }}
        >
          {PAGE_CONSTANTS.BODY}
        </DsTypography>

        <DsStack
          direction={{ xs: 'column', md: 'row' }}
          spacing='var(--ds-spacing-bitterCold)'
          sx={{ width: '100%' }}
        >
          {this.renderStepCard(
            'ri-user-2-line',
            PAGE_CONSTANTS.STEP_1_TITLE,
            PAGE_CONSTANTS.STEP_1_BODY
          )}
          {this.renderStepCard(
            'ri-lock-password-line',
            PAGE_CONSTANTS.STEP_2_TITLE,
            PAGE_CONSTANTS.STEP_2_BODY
          )}
          {this.renderStepCard(
            'ri-book-open-line',
            PAGE_CONSTANTS.STEP_3_TITLE,
            PAGE_CONSTANTS.STEP_3_BODY
          )}
        </DsStack>

        <DsStack
          direction='row'
          spacing='var(--ds-spacing-glacial)'
          alignItems='center'
          justifyContent='center'
          sx={{
            mt: 'var(--ds-spacing-pleasant)',
            pt: 'var(--ds-spacing-glacial)',
            borderTop: '1px solid var(--ds-colour-strokeDefault)',
            color: 'text.secondary',
            textAlign: 'center'
          }}
        >
          <DsRemixIcon
            className='ri-shield-check-line'
            style={{
              fontSize: '20px',
              color: 'var(--ds-colour-strokeSelected)',
              marginRight: 'var(--ds-spacing-deepFreeze)'
            }}
          />
          <DsTypography variant='bodyRegularSmall'>
            {PAGE_CONSTANTS.FOOTER}
          </DsTypography>
        </DsStack>
      </DsStack>
    )
  }

  render() {
    const { PAGE_CONSTANTS, authUser, platformUsers, isLoading } = this.props
    const { localDismissed, hasFetched } = this.state

    const childrenList = platformUsers?.CHILD || []
    const isDismissed = localDismissed

    // Only show popup when API has either finished fetching at least once in this component's mount,
    // or children are already loaded, AND there are exactly zero children, AND it's not dismissed.
    const showPopup =
      (hasFetched || (!isLoading && childrenList.length > 0)) &&
      childrenList.length === 0 &&
      !isDismissed

    console.log('AdultNoChildrenWelcomePopup state & props:', {
      hasFetched,
      isLoading,
      childrenListLength: childrenList.length,
      isDismissed,
      showPopup,
      authUserId: authUser?.id
    })

    if (!showPopup) {
      return null
    }

    return (
      <DsPopup
        open={showPopup}
        onClose={this.handleClose}
        DsBottomSheetProps={{
          // kicker: PAGE_CONSTANTS.KICKER,
          title: PAGE_CONSTANTS.TITLE,
          primaryButtonText: PAGE_CONSTANTS.PRIMARY_CTA,
          primaryButtonProps: { onClick: this.handleCreateChildClick },
          secondaryButtonText: PAGE_CONSTANTS.SECONDARY_CTA,
          secondaryButtonProps: { onClick: this.handleClose },
          children: (
            <DsBox sx={{ pb: 'var(--ds-spacing-bitterCold)' }}>
              {this.renderPopupContent()}
            </DsBox>
          ),
          PaperProps: {
            sx: {
              maxHeight: 'calc(var(--vh) - 16px)',
              height: 'calc(var(--vh) - 16px)'
            }
          }
        }}
        DsDialogProps={{
          primaryButtonText: PAGE_CONSTANTS.PRIMARY_CTA,
          primaryButtonProps: { onClick: this.handleCreateChildClick },
          secondaryButtonText: PAGE_CONSTANTS.SECONDARY_CTA,
          secondaryButtonProps: { onClick: this.handleClose },
          PaperProps: {
            sx: {
              maxWidth: {
                xs: 'calc(100% - var(--ds-spacing-warm))',
                md: '800px'
              }
            }
          },
          children: (
            <DsBox sx={{ px: 'var(--ds-spacing-warm)' }}>
              <PageTitle
                // kicker={PAGE_CONSTANTS.KICKER}
                kicker=''
                title={PAGE_CONSTANTS.TITLE}
                BoxProps={{
                  sx: { mb: 'var(--ds-spacing-glacial)' }
                }}
              />
              {this.renderPopupContent()}
            </DsBox>
          )
        }}
      />
    )
  }
}

const mapStateToProps = (state: any) => {
  const isLoading = isServiceLoading(state, [getMyChildrenServiceName])
  const platformUsers = getPlatformUsersSelector(state)
  const authUser = getUserReducer(state)
  return { isLoading, platformUsers, authUser }
}

const mapDispatchToProps = (dispatch: ThunkDispatch<any, any, any>) => ({
  actions: {
    getMyChildrenService: () => dispatch(getMyChildrenService())
  }
})

const connector = connect(mapStateToProps, mapDispatchToProps)
type PropsFromRedux = ConnectedProps<typeof connector>

export default withPageConstants<T_ADULT_WELCOME_POPUP_PAGE_CONSTANTS>(
  ADULT_WELCOME_POPUP_PAGE_CONSTANTS,
  connector(withRouter(AdultNoChildrenWelcomePopup))
)
